// pms-adapters / supabase / fiscalAudit — « Administration fiscale » (P3 F4c-2; migration 20261015150100).
//
// Every read and command is one RPC gated in the database on `pms.fiscal.audit` and the établissement. The download
// is the one place this adapter speaks HTTP to our own backend: the archive's files live in R2, which only the
// site's Function can reach. It first opens a TICKET — `pms_fiscal_archive_open` seals an `export` event in the JET —
// and the Function serves files only against that ticket, so a download cannot happen untraced (brief §11.19 Q15).
// Each file is checked against the manifest's SHA-256 before it goes into the ZIP: what the inspector receives is
// what the chain sealed, or nothing.

import { zipSync } from 'fflate';
import {
  archiveFolderName,
  err,
  ok,
  type ArchiveDownload,
  type FiscalAudit,
  type FiscalAuditArchive,
  type FiscalAuditOverview,
  type FiscalInspector,
  type FiscalVerificationRun,
  type InspectorAccessInput,
  type InspectorAccessOpened,
  type IsoDate,
  type PropertyCode,
  type Result,
} from '@lemontaigu/pms-core';
import { toPmsError, type SupabaseClient } from './support.js';

/** How the adapter reaches the download Function and the caller's session (the ChannelOpsConfig pattern). */
export interface FiscalArchiveFilesConfig {
  /** Origin serving the PMS Functions (the SITE CF Pages project). */
  apiBase: string;
  /** The caller's own access token, read at call time. */
  accessToken(): Promise<string | null>;
}

async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const d = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as Uint8Array<ArrayBuffer>));
  return Array.from(d, (b) => b.toString(16).padStart(2, '0')).join('');
}

export class SupabaseFiscalAudit implements FiscalAudit {
  constructor(
    private readonly db: SupabaseClient,
    private readonly files: FiscalArchiveFilesConfig | null,
    private readonly fetchImpl: typeof fetch = (input, init) => fetch(input, init),
  ) {}

  private async rpc<T>(fn: string, args: Record<string, unknown>): Promise<Result<T>> {
    const r = await this.db.rpc(fn, args);
    if (r.error) return { ok: false, error: toPmsError(r.error) };
    return ok(r.data as T);
  }

  overview(propertyCode: PropertyCode, from: IsoDate, to: IsoDate): Promise<Result<FiscalAuditOverview>> {
    return this.rpc('pms_fiscal_audit_overview', { p_property_code: propertyCode, p_from: from, p_to: to });
  }

  startVerification(caisseId: number): Promise<Result<FiscalVerificationRun>> {
    return this.rpc('pms_fiscal_integrity_start', { p_caisse_id: caisseId });
  }

  stepVerification(runId: number): Promise<Result<FiscalVerificationRun>> {
    return this.rpc('pms_fiscal_integrity_step', { p_run_id: runId });
  }

  requestArchive(caisseId: number, from: IsoDate, to: IsoDate, reason: string | null): Promise<Result<number>> {
    return this.rpc('pms_fiscal_request_archive', { p_caisse_id: caisseId, p_from: from, p_to: to, p_reason: reason });
  }

  async downloadArchive(
    archive: FiscalAuditArchive,
    caisseCode: string,
    onFile?: (done: number, total: number) => void,
  ): Promise<Result<ArchiveDownload>> {
    if (!this.files) {
      return err('validation', "Le téléchargement des archives n'est pas configuré dans cette application", { retryable: false });
    }
    const opened = await this.rpc<{ ticket: number; manifestSha256: string }>('pms_fiscal_archive_open', {
      p_archive_id: archive.id,
    });
    if (!opened.ok) return opened;
    const token = await this.files.accessToken();
    if (!token) return err('forbidden', 'Session expirée — reconnectez-vous.', { retryable: false });

    const get = async (path: string): Promise<Result<Uint8Array>> => {
      const url =
        `${this.files!.apiBase}/api/pms/fiscal-archive?archive=${archive.id}` +
        `&ticket=${opened.value.ticket}&path=${encodeURIComponent(path)}`;
      let res: Response;
      try {
        res = await this.fetchImpl(url, { headers: { Authorization: `Bearer ${token}` } });
      } catch (e) {
        return err('transient', `Service des archives injoignable (${e instanceof Error ? e.message : String(e)})`, {
          retryable: true,
          cause: e,
        });
      }
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as { error?: string } | null;
        return err(res.status === 401 || res.status === 403 ? 'forbidden' : 'integration',
          body?.error ?? `${path} : refusé (${res.status})`, { retryable: res.status >= 500 });
      }
      return ok(new Uint8Array(await res.arrayBuffer()));
    };

    const manifestBytes = await get('manifest.json');
    if (!manifestBytes.ok) return manifestBytes;
    if ((await sha256Hex(manifestBytes.value)) !== opened.value.manifestSha256) {
      return err('integration', "Le manifeste reçu n'est pas celui que la chaîne a scellé — téléchargement interrompu");
    }
    let listed: Array<{ path: string; sha256: string }>;
    try {
      listed = (JSON.parse(new TextDecoder().decode(manifestBytes.value)) as { files: Array<{ path: string; sha256: string }> }).files;
    } catch (e) {
      return err('integration', 'Manifeste illisible', { cause: e });
    }

    const folder = archiveFolderName(caisseCode, archive);
    const entries: Record<string, Uint8Array> = { [`${folder}/manifest.json`]: manifestBytes.value };
    const total = listed.length + 2;
    let done = 1;
    onFile?.(done, total);
    for (const f of listed) {
      const got = await get(f.path);
      if (!got.ok) return got;
      if ((await sha256Hex(got.value)) !== f.sha256) {
        return err('integration', `${f.path} n'est pas le fichier que le manifeste a haché — téléchargement interrompu`);
      }
      entries[`${folder}/${f.path}`] = got.value;
      onFile?.(++done, total);
    }
    // The seal (signature + receipts) is not in the manifest — it is written after, and proves the manifest.
    const seal = await get('seal.json');
    if (!seal.ok) return seal;
    entries[`${folder}/seal.json`] = seal.value;
    onFile?.(++done, total);

    return ok({ fileName: `${folder}.zip`, bytes: zipSync(entries) });
  }

  async inspectors(propertyCode: PropertyCode): Promise<Result<FiscalInspector[]>> {
    const r = await this.db
      .from('pms_fiscal_inspector')
      .select('id, property_code, email, full_name, authority, granted_at, expires_at, revoked_at, withdrawn_at')
      .eq('property_code', propertyCode)
      .order('id', { ascending: false });
    if (r.error) return { ok: false, error: toPmsError(r.error) };
    return ok(
      (r.data ?? []).map((x: Record<string, unknown>) => ({
        id: Number(x.id),
        propertyCode: String(x.property_code),
        email: String(x.email),
        fullName: String(x.full_name),
        authority: (x.authority as string | null) ?? null,
        grantedAt: String(x.granted_at),
        expiresAt: String(x.expires_at),
        revokedAt: (x.revoked_at as string | null) ?? null,
        withdrawnAt: (x.withdrawn_at as string | null) ?? null,
      })),
    );
  }

  async openInspector(input: InspectorAccessInput): Promise<Result<InspectorAccessOpened>> {
    if (!this.files) {
      return err('validation', "L'ouverture d'accès n'est pas configurée dans cette application", { retryable: false });
    }
    const token = await this.files.accessToken();
    if (!token) return err('forbidden', 'Session expirée — reconnectez-vous.', { retryable: false });
    let res: Response;
    try {
      res = await this.fetchImpl(`${this.files.apiBase}/api/pms/inspector-access`, {
        method: 'POST',
        headers: { 'content-type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(input),
      });
    } catch (e) {
      return err('transient', `Service injoignable (${e instanceof Error ? e.message : String(e)})`, { retryable: true, cause: e });
    }
    const body = (await res.json().catch(() => null)) as (InspectorAccessOpened & { error?: string }) | null;
    if (!res.ok || !body || typeof body.password !== 'string') {
      return err(res.status === 401 || res.status === 403 ? 'forbidden' : res.status === 400 ? 'validation' : 'integration',
        body?.error ?? `Ouverture refusée (${res.status})`);
    }
    return ok({ inspectorId: body.inspectorId, email: body.email, password: body.password, expiresAt: body.expiresAt });
  }

  async revokeInspector(inspectorId: number): Promise<Result<void>> {
    const r = await this.db.rpc('pms_fiscal_inspector_revoke', { p_id: inspectorId });
    if (r.error) return { ok: false, error: toPmsError(r.error) };
    return ok(undefined);
  }
}
